import { Avatar, BottomSheet, EmptyState, ErrorState, FilterChips, Icon, LargeTitle, PullToRefresh, Skeleton, SwipeRow, useToast } from "@helmr/ui";
import type { LeverId } from "@helmr/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { isApiError } from "../../api/client";
import { patchConversation, type Conversation, type InboxNumber, type InboxSegment } from "../../api/inbox";
import { useLargeTitleChrome } from "../../app/mobile/chrome";
import { useServices } from "../../app/services";
import { FailureBody } from "../home/states";
import { useSheetBack } from "../../app/mobile/useSheetBack";
import { firstName, listTime } from "./format";
import { FILTERS, SEGMENTS, useConversationList, useCounts, useDebouncedValue, useInboxAccess, useInboxParams, useNow, useNumbers, type InboxFilter } from "./hooks";
import { StatusTick } from "./StatusTick";
import { windowState } from "./window";
import { inbox as t } from "./strings";
import s from "./inbox.module.css";

function WindowBadge({ c, now }: { c: Conversation; now: number }) {
  const w = windowState(c.windowOpenUntil, now);
  return w.open
    ? <span className={s.wn} data-open="true"><Icon name="clock" size={13} />{w.label}</span>
    : <span className={s.wn} data-open="false"><Icon name="lock" size={13} />{t.window.closed}</span>;
}

function Row({ c, now, search, active, onAction, archivedView, me }: { me: string | undefined; c: Conversation; now: number; search: string; active: boolean; onAction: (c: Conversation, a: "read" | "unread" | "archive" | "unarchive") => void; archivedView: boolean }) {
  const unread = c.unreadCount > 0;
  const name = c.name ?? t.newContact;
  return (
    <SwipeRow
      name={name}
      right={{ label: unread ? t.swipe.read : t.swipe.unread, icon: unread ? "check" : "mail", tone: "read", keep: true, onCommit: () => onAction(c, unread ? "read" : "unread") }}
      left={{ label: archivedView ? t.swipe.unarchive : t.swipe.archive, icon: "archive", tone: "archive", onCommit: () => onAction(c, archivedView ? "unarchive" : "archive") }}
    >
      <Link to={`/inbox/${c.id}${search}`} className={s.ir} data-unread={unread || undefined} data-active={active || undefined} data-testid="conversation-row" aria-label={`${name}${unread ? `, ${c.unreadCount} unread` : ""}`}>
        <Avatar id={c.id} name={name} />
        <span className={s.irBody}>
          <span className={s.l1}><b>{name}</b><span className={s.tm}>{c.lastMessageAt ? <RowTime at={c.lastMessageAt} now={now} /> : null}</span></span>
          <span className={s.l2}>
            <span className={s.pv}>{c.lastDirection === "out" && c.lastStatus ? <StatusTick status={c.lastStatus} /> : null}<span>{c.lastPreview ?? ""}</span></span>
            {unread ? <span className={s.ucnt}>{c.unreadCount > 99 ? "99+" : c.unreadCount}</span> : null}
          </span>
          <span className={s.l3}>
            {c.record ? <span className={s.rec} data-tone={c.record.tone ?? "neutral"}>{c.record.label}</span> : c.kind === "unknown" ? <span className={s.rec} data-tone="neutral">{t.newContact}</span> : null}
            <WindowBadge c={c} now={now} />
            <span className={s.asg}>{c.assignee ? <><Avatar id={c.assignee.id} name={c.assignee.name} size="xs" />{c.assignee.id === me ? t.assignee.you : firstName(c.assignee.name)}</> : t.assignee.none}</span>
          </span>
        </span>
      </Link>
    </SwipeRow>
  );
}

function RowTime({ at, now }: { at: number; now: number }) {
  const { ctx } = useInboxAccess();
  return <>{listTime(at, now, ctx.timeZone, ctx.locale)}</>;
}

const SkeletonList = () => (
  <div className={s.grp} aria-busy="true" aria-label={t.loading}>
    {[0, 1, 2, 3, 4].map((i) => <div className={s.skRow} key={i}><Skeleton width={40} height={40} style={{ borderRadius: "50%" }} /><div><Skeleton width="45%" height={14} /><Skeleton width="85%" height={13} /><Skeleton width="60%" height={12} /></div></div>)}
  </div>
);

function emptyCopy(seg: InboxSegment, filter: InboxFilter, archived: boolean, term: string) {
  if (term) return { title: t.empty.search(term), body: t.empty.filterBody };
  if (archived) return { title: t.empty.archived, body: "" };
  if (filter === "unread") return { title: t.empty.unread(seg), body: t.empty.filterBody };
  if (filter === "mine") return { title: t.empty.mine, body: t.empty.filterBody };
  if (filter === "unassigned") return { title: t.empty.unassigned, body: t.empty.filterBody };
  return { title: t.empty.base(seg), body: t.empty.baseBody };
}

/** The server's per-tab phone lever (D57): a lead's, a member's or a colleague's number. */
const SEGMENT_PHONE = { lead: "leads.see_phone", member: "members.see_phone", staff: "staff.see_phone" } as const satisfies Record<InboxSegment, LeverId>;

export function InboxList({ phone, activeId }: { phone: boolean; activeId: string | undefined }) {
  const { ctx, canManage } = useInboxAccess();
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const { params, set, search } = useInboxParams();
  const now = useNow();
  const [term, setTerm] = useState(params.q);
  const debounced = useDebouncedValue(term.trim(), 300);
  const seesPhone = ctx.has(SEGMENT_PHONE[params.seg]);
  // Phone search needs the tab's own phone lever (as the server checks); without it digits are not sent, so a masked number can never be probed.
  const effectiveTerm = seesPhone || !/\d/.test(debounced) ? debounced : "";
  useEffect(() => { if (debounced !== params.q) set({ q: debounced }); }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps
  const [numberOpen, setNumberOpen] = useState(false);
  const [epoch, setEpoch] = useState(0);

  const numbers = useNumbers();
  const counts = useCounts(params.num);
  const list = useConversationList(params, effectiveTerm);
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.conversations) ?? [], [list.data]);
  // A number from an old link or another branch would filter the list while the selector reads "All numbers".
  useEffect(() => { if (params.num && numbers.data && !numbers.data.some((n) => n.brandId === params.num)) set({ num: null }); }, [params.num, numbers.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const multi = (numbers.data?.length ?? 0) > 1;
  const current = numbers.data?.find((n) => n.brandId === params.num);
  const branch = ctx.me?.branch?.name ?? "";
  const onUnder = useLargeTitleChrome(t.title);

  const act = useMutation({
    mutationFn: ({ c, a }: { c: Conversation; a: "read" | "unread" | "archive" | "unarchive" }) =>
      patchConversation(client, c.id, a === "read" ? { read: true } : a === "unread" ? { read: false } : { archived: a === "archive" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["inbox"] }),
    onError: (err) => { setEpoch((n) => n + 1); toast.show(isApiError(err) && err.isForbidden ? t.info.updateFailed : t.info.updateFailed); },
  });
  const onAction = (c: Conversation, a: "read" | "unread" | "archive" | "unarchive") => {
    if (!canManage) { setEpoch((n) => n + 1); toast.show(t.info.updateFailed); return; }
    act.mutate({ c, a });
    if (a === "archive") toast.show(t.info.archived);
    if (a === "unarchive") toast.show(t.info.unarchived);
  };

  const notConnected = isApiError(list.error) && list.error.status === 404;
  const copy = emptyCopy(params.seg, params.filter, params.archived, effectiveTerm);

  const header = (
    <>
      {phone ? <LargeTitle eyebrow={t.eyebrow(branch)} title={t.title} onUnderBarChange={onUnder} /> : (
        <div className={s.dhead}><p>{t.eyebrow(branch)}</p><h1 tabIndex={-1}>{t.title}</h1></div>
      )}
      {notConnected ? null : (
        <>
          <div className={s.iseg}>
            <div className={s.seg} role="tablist" aria-label={t.segLabel}>
              {SEGMENTS.map((seg) => {
                const n = counts.data?.[seg].unread ?? 0;
                return (
                  <button key={seg} type="button" role="tab" aria-selected={params.seg === seg} aria-label={n ? t.segUnread(t.segments[seg], n) : undefined} onClick={() => set({ seg })}>
                    {t.segments[seg]}{n ? <span className={s.c}>{n}</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
          <div className={s.isr}>
            <label className={s.sf}>
              <Icon name="search" size={16} />
              <input type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder={seesPhone ? t.search.namePhone : t.search.name} aria-label={t.search.label} autoComplete="off" enterKeyHint="search" />
            </label>
            {!seesPhone && /\d/.test(term) ? <p className={s.note}>{t.search.phoneNeedsAccess}</p> : null}
          </div>
          <div className={s.chipsRow}>
            <FilterChips
              label={t.filterLabel}
              value={params.filter}
              onChange={(filter) => set({ filter })}
              options={FILTERS.map((f) => ({ value: f, label: t.filters[f] }))}
              leading={multi ? (
                <button type="button" className={s.nsel} aria-haspopup="dialog" aria-label={t.number.label(current?.brandName ?? t.number.all)} onClick={() => setNumberOpen(true)}>
                  <Icon name="phone" size={16} />{current?.brandName ?? t.number.all}<Icon name="down" size={14} />
                </button>
              ) : undefined}
            />
          </div>
          {phone ? <p className={s.hintLine}><Icon name="chevR" size={14} />{t.swipe.hint}</p> : null}
        </>
      )}
    </>
  );

  return (
    <section className={s.listCol} aria-label={t.title} data-phone={phone || undefined}>
      <PullToRefresh disabled={!phone || !!activeId} onRefresh={() => qc.invalidateQueries({ queryKey: ["inbox"] })}>
        {header}
        <div className={s.listBody}>
          {list.isPending ? <SkeletonList /> : notConnected ? (
            <div className={s.grp}><FailureBody error={list.error} title={t.error} what={t.notConnected} onRetry={() => void list.refetch()} /></div>
          ) : list.error ? (
            <div className={s.grp}>{isApiError(list.error) && list.error.status === 403 ? <FailureBody error={list.error} title={t.error} what={t.notConnected} onRetry={() => void list.refetch()} /> : <ErrorState compact title={t.error} message={isApiError(list.error) ? list.error.message : ""} requestId={isApiError(list.error) ? list.error.requestId : null} onRetry={() => void list.refetch()} />}</div>
          ) : items.length === 0 ? (
            <div className={s.grp}><EmptyState compact icon="chat" title={copy.title} description={copy.body || undefined} /></div>
          ) : (
            <>
              <div className={s.grp} data-testid="conversation-list">
                {items.map((c) => <Row key={`${c.id}:${epoch}`} c={c} now={now} search={search} active={c.id === activeId} onAction={onAction} archivedView={params.archived} me={ctx.me?.staff.id} />)}
              </div>
              {list.hasNextPage ? <button type="button" className={s.more} onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>{list.isFetchingNextPage ? "Loading…" : t.loadMore}</button> : null}
            </>
          )}
          {notConnected ? null : <button type="button" className={s.arcl} onClick={() => set({ archived: !params.archived })}><Icon name="archive" size={16} />{params.archived ? t.activeLink : t.archivedLink}</button>}
        </div>
      </PullToRefresh>
      <NumberSheet open={numberOpen} onOpenChange={setNumberOpen} numbers={numbers.data ?? []} value={params.num} onPick={(num) => { set({ num }); setNumberOpen(false); }} />
    </section>
  );
}

function NumberSheet({ open, onOpenChange, numbers, value, onPick }: { open: boolean; onOpenChange: (o: boolean) => void; numbers: InboxNumber[]; value: string | null; onPick: (n: string | null) => void }) {
  useSheetBack(open, () => onOpenChange(false));
  const rows: Array<{ id: string | null; name: string; sub: string }> = [{ id: null, name: t.number.all, sub: t.number.allSub }, ...numbers.map((n) => ({ id: n.brandId, name: n.brandName, sub: n.displayPhone }))];
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={t.number.title} description={t.number.sub}>
      <div role="listbox" aria-label={t.number.title} className={s.lst}>
        {rows.map((r) => (
          <button key={r.id ?? "all"} type="button" role="option" aria-selected={value === r.id} className={s.opt} onClick={() => onPick(r.id)}>
            <span className={s.optText}><b>{r.name}</b><small>{r.sub}</small></span>
            {value === r.id ? <Icon name="check" className={s.ck} /> : null}
          </button>
        ))}
      </div>
    </BottomSheet>
  );
}
